// src/pages/Dashboard.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Typography,
  Grid,
  Box,
  Container,
  Skeleton,
  Alert,
  Chip,
  Stack,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import SystemCard from "../components/SystemCard";
import { useAuth } from "../context/AuthContext";
import { getSystems } from "../services/systemService";
import { getTodaysUpdates } from "../services/dashboardService";
import SupportTicketsPanel from "../components/SupportTicketsPanel";

const PREVIEW_LIMIT = 3;
const UPDATES_PAGE_LIMIT = 3;
const UPDATES_MODAL_LIMIT = 100;

const LAST_ACCESS_KEY = "envoy:lastSystemAccess";

function formatDateHeader(d = new Date()) {
  // Example: Monday, Feb 24
  return d.toLocaleDateString("en-AU", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

const Dashboard = () => {
  const { user, token } = useAuth();

  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastAccess, setLastAccess] = useState(null);

  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(true);
  const [updatesModalOpen, setUpdatesModalOpen] = useState(false);
  const [fullUpdates, setFullUpdates] = useState([]);
  const [fullUpdatesLoading, setFullUpdatesLoading] = useState(false);

  // Load last accessed system
  useEffect(() => {
    const stored = localStorage.getItem(LAST_ACCESS_KEY);
    if (stored) {
      try {
        setLastAccess(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse last access", e);
      }
    }
  }, []);

  // Load system list
  useEffect(() => {
    const fetchSystems = async () => {
      try {
        const data = await getSystems(token);
        setSystems(data || []);
      } catch (err) {
        setError("Failed to load systems");
      } finally {
        setLoading(false);
      }
    };
    fetchSystems();
  }, [token]);

  // Load today's updates (preview of 3)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setUpdatesLoading(true);
      try {
        const { updates: list } = await getTodaysUpdates({ limit: UPDATES_PAGE_LIMIT });
        if (!cancelled) setUpdates(list);
      } catch (e) {
        if (!cancelled) setUpdates([]);
      } finally {
        if (!cancelled) setUpdatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleOpenUpdatesModal = useCallback(async () => {
    setUpdatesModalOpen(true);
    setFullUpdatesLoading(true);
    try {
      const { updates: list } = await getTodaysUpdates({ limit: UPDATES_MODAL_LIMIT });
      setFullUpdates(list);
    } catch (e) {
      setFullUpdates([]);
    } finally {
      setFullUpdatesLoading(false);
    }
  }, []);

  const handleCloseUpdatesModal = useCallback(() => {
    setUpdatesModalOpen(false);
  }, []);

  // Track last accessed system
  const handleSystemAccess = (system) => {
    const payload = {
      id: system.id,
      name: system.name,
      accessedAt: new Date().toISOString(),
    };
    setLastAccess(payload);
    localStorage.setItem(LAST_ACCESS_KEY, JSON.stringify(payload));
  };

  const displayName =
    user?.name || user?.email?.split("@")?.[0] || "Admin";

  const allLiveLabel = useMemo(() => {
    if (!systems?.length) return "No Systems";
    return "All Systems Live";
  }, [systems?.length]);

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, pt: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="text" width={260} height={44} />
            <Skeleton variant="text" width={420} height={26} />
          </Box>

          <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1, mb: 5 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={210}
                height={120}
                sx={{ borderRadius: 2, flexShrink: 0 }}
              />
            ))}
          </Box>

          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item key={i} xs={12} sm={6} md={4}>
                <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, pt: 0, pb: 6 }}>
      <Container maxWidth="lg">
        {/* Header (inspiration-style) */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 400 }}
            >
              {formatDateHeader()}
            </Typography>

            <Typography
              variant="h4"
              sx={{
                mt: 0.5,
                fontWeight: 500,
                letterSpacing: "-0.6px",
                lineHeight: 1.1,
              }}
            >
              Hi, {displayName}
            </Typography>

            {user?.companies?.name && (
              <Typography
                variant="h6"
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  mt: 0.5,
                  letterSpacing: "-0.2px",
                }}
              >
                {user.companies.name}
              </Typography>
            )}

            <Typography
              variant="body1"
              sx={{ mt: 0.75, color: "text.secondary" }}
            >
              Access your connected POS, Inventory and ERS from one place.
            </Typography>
          </Box>
        </Box>

        {/* Quick metrics (horizontal scroll like inspiration) */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            pb: 1,
            mb: 4,
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {/* Primary metric card */}
          <Box
            sx={{
              flexShrink: 0,
              width: 240,
              borderRadius: 2,
              p: 2,
              color: "#fff",
              backgroundColor: "primary.main",
              boxShadow: "0 10px 26px rgba(37,99,235,0.25)",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                textTransform: "uppercase",
                fontWeight: 500,
                opacity: 0.9,
                letterSpacing: 0.8,
              }}
            >
              Connected Systems
            </Typography>

            <Typography sx={{ mt: 0.75, fontSize: 26, fontWeight: 500, lineHeight: 1 }}>
              {systems.length}
            </Typography>

            <Typography sx={{ mt: 1, fontSize: 13, opacity: 0.9 }}>
              Ready to access
            </Typography>
          </Box>

          {/* On Clock / status style card */}
          <Box
            sx={{
              flexShrink: 0,
              width: 240,
              borderRadius: 2,
              p: 2,
              bgcolor: "background.paper",
              border: "1px solid rgba(0,0,0,0.10)",
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                textTransform: "uppercase",
                fontWeight: 500,
                color: "text.secondary",
                letterSpacing: 0.8,
              }}
            >
              System Status
            </Typography>

            <Typography sx={{ mt: 0.75, fontSize: 20, fontWeight: 600 }}>
              {allLiveLabel}
            </Typography>

            <Chip
              label="Live"
              size="small"
              sx={{
                mt: 1,
                bgcolor: "rgba(16,185,129,0.10)",
                color: "#065f46",
                border: "1px solid rgba(16,185,129,0.25)",
              }}
            />
          </Box>

          {/* Last accessed */}
          <Box
            sx={{
              flexShrink: 0,
              width: 240,
              borderRadius: 2,
              p: 2,
              bgcolor: "background.paper",
              border: "1px solid rgba(0,0,0,0.10)",
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 6,
                bgcolor: "primary.main",
              }}
            />
            <Typography
              sx={{
                pl: 1,
                fontSize: 11,
                textTransform: "uppercase",
                fontWeight: 600,
                color: "text.secondary",
                letterSpacing: 0.8,
              }}
            >
              Last Accessed
            </Typography>

            <Typography sx={{ pl: 1, mt: 0.75, fontSize: 18, fontWeight: 600 }}>
              {lastAccess?.name || "—"}
            </Typography>

            <Typography sx={{ pl: 1, mt: 0.75, fontSize: 13, color: "text.secondary" }}>
              {lastAccess?.accessedAt
                ? new Date(lastAccess.accessedAt).toLocaleString("en-AU", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                : "No recent access"}
            </Typography>
          </Box>
        </Box>

        {/* Error */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              border: "1px solid rgba(239,68,68,0.25)",
            }}
          >
            {error}
          </Alert>
        )}

        {/* Subsystems (your real systems list) */}
        <Box sx={{ mt: 2, mb: 2.5, display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ width: 6, height: 26, borderRadius: 999, bgcolor: "primary.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Subsystems
          </Typography>
        </Box>

        {systems.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              border: "1px solid rgba(37,99,235,0.25)",
            }}
          >
            No systems available for your role. Please contact an administrator.
          </Alert>
        ) : (
          <Grid
            container
            spacing={4}
            sx={{
              mb: 5,
              "& > .MuiGrid-item > *": {
                transition: "transform 0.18s ease, box-shadow 0.18s ease",
              },
            }}
          >
            {systems.map((system) => (
              <Grid item key={system.id} xs={12} sm={6} md={4}>
                <SystemCard system={system} onAccess={handleSystemAccess} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Today's Updates (wired feed) */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 6, height: 26, borderRadius: 999, bgcolor: "primary.main" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Today&apos;s Updates
            </Typography>
          </Box>

          <Button
            size="small"
            sx={{ fontWeight: 500, color: "primary.main" }}
            onClick={handleOpenUpdatesModal}
          >
            View All
          </Button>
        </Box>

        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.10)",
            overflow: "hidden",
            mb: 4,
          }}
        >
          {updatesLoading ? (
            [1, 2, 3].map((i) => (
              <Box key={i}>
                <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <Skeleton variant="circular" width={10} height={10} sx={{ mt: 0.9, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
                {i !== 3 && <Divider />}
              </Box>
            ))
          ) : updates.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No updates for today yet.
              </Typography>
            </Box>
          ) : (
            updates.slice(0, PREVIEW_LIMIT).map((row, idx) => (
              <Box key={row.id ?? idx}>
                <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      bgcolor: row.dot || "#2563eb",
                      mt: 0.9,
                      boxShadow: `0 0 0 6px ${(row.dot || "#2563eb")}14`,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>{row.title}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
                      {row.sub}
                    </Typography>
                  </Box>
                </Box>
                {idx < Math.min(updates.length, PREVIEW_LIMIT) - 1 && <Divider />}
              </Box>
            ))
          )}
        </Box>

        {/* Today's Updates – full list modal */}
        <Dialog
          open={updatesModalOpen}
          onClose={handleCloseUpdatesModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Today&apos;s Updates
            </Typography>
            <IconButton aria-label="close" onClick={handleCloseUpdatesModal} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {fullUpdatesLoading ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Box key={i} sx={{ py: 1.5, display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Skeleton variant="circular" width={10} height={10} sx={{ mt: 0.9, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="70%" />
                      <Skeleton variant="text" width="50%" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : fullUpdates.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No updates for today.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ py: 0 }}>
                {fullUpdates.map((row, idx) => (
                  <Box key={row.id ?? idx}>
                    <Box sx={{ px: 2, py: 1.5, display: "flex", gap: 2, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          bgcolor: row.dot || "#2563eb",
                          mt: 0.9,
                          boxShadow: `0 0 0 6px ${(row.dot || "#2563eb")}14`,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 700 }}>{row.title}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
                          {row.sub}
                        </Typography>
                        {row.reason && (
                          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5, fontStyle: "italic" }}>
                            {row.reason}
                          </Typography>
                        )}
                        {row.message && (
                          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                            {row.message}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {idx < fullUpdates.length - 1 && <Divider />}
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Support Tickets (UI-only cards) */}
        <Box sx={{ mt: 4 }}>
          <SupportTicketsPanel />
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;